import { PopoverButton, PopoverPanel, Portal } from '@headlessui/react';
import { DocumentTextIcon as DocumentTextIconSolid } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';

import type { Note } from '@/apps/Notes/stores/useNotesStore';
import type { WidgetLink } from '@/common/stores/useWidgetLinksStore';

import cn from '@/common/utils/cn';

interface LinkedNotesPopoverProps {
  fetchNotesByIds: (ids: string[]) => Promise<Note[]>;
  getButtonTitle: () => string;
  hasNotesWidget: boolean;
  linkedLinks: WidgetLink[];
  open: boolean;
  onEditNoteClick?: (note: Note) => void;
}

export const LinkedNotesPopover = ({
  fetchNotesByIds,
  getButtonTitle,
  hasNotesWidget,
  linkedLinks,
  open,
  onEditNoteClick,
}: LinkedNotesPopoverProps) => {
  const [linkedNotes, setLinkedNotes] = useState<Note[]>([]);

  useEffect(() => {
    if (!open || linkedLinks.length === 0) return;

    const ids = linkedLinks.map((link) => link.target_id);
    fetchNotesByIds(ids).then(setLinkedNotes);
  }, [open, linkedLinks, fetchNotesByIds]);

  return (
    <>
      <PopoverButton
        className={cn(
          'p-1 rounded transition-colors',
          hasNotesWidget
            ? 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            : 'text-gray-400 cursor-not-allowed',
        )}
        disabled={!hasNotesWidget}
        title={getButtonTitle()}
      >
        <DocumentTextIconSolid className="w-4 h-4 text-amber-500" />
      </PopoverButton>
      <Portal>
        <PopoverPanel
          anchor="bottom start"
          className="z-50 mt-1 w-64 rounded-lg bg-white p-3 shadow-lg border border-gray-100"
        >
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase">
              {linkedLinks.length} linked note{linkedLinks.length > 1 ? 's' : ''}
            </p>
            {linkedNotes.map((note) => (
              <button
                key={note.id}
                className={cn(
                  'w-full text-left p-2 rounded bg-gray-50 text-sm transition-colors',
                  onEditNoteClick && 'hover:bg-gray-100 cursor-pointer',
                )}
                type="button"
                onClick={() => onEditNoteClick?.(note)}
              >
                <p className="font-medium text-gray-800 truncate">{note.title || 'Untitled'}</p>
                <p
                  dangerouslySetInnerHTML={{
                    __html: (note.content || '')
                      .replace(/<[^>]*>/g, ' ')
                      .slice(0, 100),
                  }}
                  className="text-gray-600 text-xs line-clamp-2 mt-0.5"
                />
              </button>
            ))}
          </div>
        </PopoverPanel>
      </Portal>
    </>
  );
};
